import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { fetchDsos, updateDsoNetTerms } from '../../../../services/dsos';
import { showErrorMessage, showSuccessMessage } from '../../../../utils';

const DSO_CONTROLS_QUERY_KEY = 'DSO_CONTROLS';

export const useDsoControls = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const limit = 25;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [DSO_CONTROLS_QUERY_KEY, page, search],
    queryFn: () => fetchDsos({ page, limit, search: search || undefined }),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    initialData: { data: [], total: 0 },
  });

  const mutation = useMutation({
    mutationFn: updateDsoNetTerms,
    onSuccess: (_, variables) => {
      showSuccessMessage({ message: 'Net Terms was updated successfully' });
      queryClient.setQueryData([DSO_CONTROLS_QUERY_KEY, page, search], (old) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.map((dso) =>
            dso.id === variables.id ? { ...dso, netTerms: variables.netTerms } : dso,
          ),
        };
      });
    },
    onError: (error) => {
      const status = error?.response?.status;
      if (status === 403) {
        showErrorMessage({
          message: "This office's net terms is assigned on a DSO-level and cannot be changed.",
        });
        return;
      }
      showErrorMessage({
        message: error?.response?.data?.message || 'Failed to update Net Terms.',
      });
    },
  });

  const updateNetTerms = (id, netTerms) => {
    mutation.mutate({ id, netTerms });
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return {
    dsos: data?.data || [],
    isLoading: isLoading || isFetching,
    page,
    setPage,
    search,
    setSearch,
    totalPages,
    total: data?.total || 0,
    updateNetTerms,
    isUpdating: mutation.isLoading,
  };
};
